import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Calendar, CalendarCheck, CalendarX, ChevronLeft, ChevronRight, Clock, FileSpreadsheet, Inbox, Info,
  Lock, LucideIcon, Plus, RefreshCw, Send, Stethoscope, Sun, Wallet, X,
} from 'lucide-react';
import {
  cancelPortalTimeOffRequest,
  createPortalTimeOffRequest,
  describePortalError,
  getPortalHolidays,
  getPortalTimeOffBalance,
  getPortalTimeOffLedger,
  getPortalTimeOffRequestOptions,
} from '../../../api/portal';
import config from '../../../configLoader';
import { isUnpaid } from '../../../util/timeOffUnpaid';
import { usePortalData } from './PortalContext';
import type { PortalErrorInfo } from './portalErrors';
import { addDaysYmd, fmtYmdDow } from './portalTime';
import type {
  PortalHoliday,
  PortalTimeOffBalance,
  PortalTimeOffLedgerEntry,
  PortalTimeOffLedgerPage,
  PortalTimeOffRequest,
  PortalTimeOffRequestOptions,
} from './portalTypes';
import {
  buildRequestBody,
  cancelState,
  categoryEarliest,
  daysLabel,
  DayMode,
  defaultCategory,
  defaultMode,
  expandRequestDays,
  f2,
  halfDayAllowed,
  HALF_DAY_HOURS,
  holidayReason,
  holidayView,
  hourOptions,
  hoursForDay,
  ledgerKind,
  MAX_DAYS_AHEAD,
  nextWorkday,
  notRequestableReason,
  pageLabel,
  preflightReason,
  projectBalance,
  rangeLabel,
  requestDays,
  requestStatus,
  RequestStatus,
  resolveHourIncrement,
  sortCategories,
  STATUS_PILL,
  submitRefusalText,
  TimeOffRefusal,
} from './timeOffModel';
import { usePortalToast } from './usePortalToast';
import './employeePortal.css';
import './myTimesheet.css';
import './myTimeOff.css';

/**
 * Employee Time Portal — Time Off (P-TAS-20 Phase 3, App-3; handoff §8, screenshots 37–43).
 * Three tabs:
 *   - Balances & request: a card per policy category (available / pending / used this year;
 *     Unpaid Time when the policy has it), a Holiday card from /timeOff/holidays (eligible, or
 *     "Eligible after <date>"), the request form (category, From/To within requestOptions'
 *     window, Full / Half / Custom per day in the tenant's steps, projected balance, note),
 *     Awaiting approval and Company holidays;
 *   - My requests: status pills, days / hours, submitted, reviewer + notes, Cancel (confirm;
 *     the server's 400 is shown when the week is submitted or exported);
 *   - Accrual history: the per-category ledger, paged.
 * Every call is /portal/timeOff/* (never /admin). The rules live in timeOffModel.ts.
 */

const LEDGER_LIMIT = 25;

const PALETTE = ['#2f7bf5', '#2fa3c2', '#d4881a', '#b1499c', '#1f9d6b'];

function catIcon(name: string, unpaid: boolean): LucideIcon {
  if (unpaid) return Calendar;
  if (/sick/i.test(name)) return Stethoscope;
  if (/vac|pto|personal/i.test(name)) return Sun;
  return CalendarCheck;
}

function catColor(index: number, unpaid: boolean): string {
  return unpaid ? '#8a93a2' : PALETTE[index % PALETTE.length];
}

/** `Oct 13, 2026` for an ISO instant, in the tenant zone. */
function fmtInstant(iso: string | null | undefined, zone: string): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (isNaN(ms)) return '';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: zone });
}

/** `Oct 13, 2026` for a calendar date. */
function fmtYmdLong(ymd: string): string {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || '?';
}

type Tab = 'balances' | 'requests' | 'ledger';

interface Loaded<T> {
  data: T | null;
  error: PortalErrorInfo | null;
}

const MyTimeOffPage: React.FC = () => {
  const {
    cfg, zone, todayYmd, self, requests, thisWeekStatus, reportSessionExpired, reloadTimeOffRequests,
  } = usePortalData();
  const toast = usePortalToast();
  const showToast = toast.show;

  const increment = resolveHourIncrement(cfg?.timeOff?.hourIncrement);
  const tenantAllowsOptional = cfg?.timeOff?.allowWeekendHolidayDays === true;
  // Role.AllowTimeOffRequests only gates tenants that turn time off on per role
  // (roleFeatureToggles.timeOffRequests, NPM). Elsewhere (20/20) mobile ignores it and
  // every role defaults to 0, so honouring it there would block everyone.
  const roleAllows = config.roleFeatureToggles?.timeOffRequests !== true
    || self?.role?.allowTimeOffRequests === true;
  const supervisorName = thisWeekStatus?.supervisorName ?? null;

  const [tab, setTab] = useState<Tab>('balances');

  /* ------------------------------------------------ loads */
  const [balances, setBalances] = useState<Loaded<PortalTimeOffBalance[]>>({ data: null, error: null });
  const [options, setOptions] = useState<Loaded<PortalTimeOffRequestOptions>>({ data: null, error: null });
  const [yearHolidays, setYearHolidays] = useState<PortalHoliday[] | null>(null);
  const [requestsError, setRequestsError] = useState<PortalErrorInfo | null>(null);
  const [requestsLoaded, setRequestsLoaded] = useState(false);

  const fail = useCallback((err: unknown): PortalErrorInfo => {
    const info = describePortalError(err);
    if (info.kind === 'sessionExpired') reportSessionExpired();
    return info;
  }, [reportSessionExpired]);

  const loadBalances = useCallback(async () => {
    try {
      setBalances({ data: sortCategories(await getPortalTimeOffBalance()), error: null });
    } catch (err) {
      setBalances((b) => ({ data: b.data, error: fail(err) }));
    }
  }, [fail]);

  const loadOptions = useCallback(async () => {
    try {
      // Far enough back for the past-date window (current + prior pay periods), a year ahead.
      const o = await getPortalTimeOffRequestOptions(addDaysYmd(todayYmd, -70), addDaysYmd(todayYmd, MAX_DAYS_AHEAD));
      setOptions({ data: o, error: null });
    } catch (err) {
      setOptions((o) => ({ data: o.data, error: fail(err) }));
    }
  }, [todayYmd, fail]);

  const loadRequests = useCallback(async () => {
    try {
      await reloadTimeOffRequests();
      setRequestsError(null);
    } catch (err) {
      setRequestsError(describePortalError(err));
    } finally {
      setRequestsLoaded(true);
    }
  }, [reloadTimeOffRequests]);

  const year = todayYmd.slice(0, 4);
  useEffect(() => {
    void loadBalances();
    void loadOptions();
    void loadRequests();
    let live = true;
    getPortalHolidays(`${year}-01-01`, `${year}-12-31`)
      .then((h) => { if (live) setYearHolidays(h); })
      .catch((err) => { fail(err); if (live) setYearHolidays([]); });
    return () => { live = false; };
  }, [loadBalances, loadOptions, loadRequests, year, fail]);

  const cats = useMemo(() => balances.data ?? [], [balances.data]);
  const paidCats = useMemo(() => cats.filter((c) => !isUnpaid(c)), [cats]);
  const hasUnpaid = cats.some((c) => isUnpaid(c));
  const colorOf = useCallback((c: PortalTimeOffBalance) => catColor(Math.max(0, paidCats.indexOf(c)), isUnpaid(c)), [paidCats]);
  const blockedPast = useMemo(() => new Set(options.data?.blockedPastDates ?? []), [options.data]);
  const holidayMap = useMemo(
    () => new Map((options.data?.excludedDates ?? []).map((e) => [e.date.slice(0, 10), e.reason] as [string, string])),
    [options.data],
  );
  const hv = useMemo(() => holidayView(yearHolidays ?? [], todayYmd), [yearHolidays, todayYmd]);

  /* ------------------------------------------------ the request form */
  const fromRef = useRef<HTMLInputElement | null>(null);
  const [catID, setCatID] = useState<number | null>(null);
  const [fromYmd, setFromYmd] = useState('');
  const [toYmd, setToYmd] = useState('');
  const [modes, setModes] = useState<Record<string, DayMode>>({});
  const [custom, setCustom] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Defaults once the categories arrive: the first category usable today, the next workday.
  useEffect(() => {
    if (catID == null && cats.length) {
      const first = defaultCategory(cats);
      if (first) setCatID(first.timeOffCategoryID);
    }
  }, [cats, catID]);
  useEffect(() => {
    if (!fromYmd && options.data) {
      const d = nextWorkday(todayYmd, holidayMap);
      setFromYmd(d);
      setToYmd(d);
    }
  }, [options.data, fromYmd, todayYmd, holidayMap]);

  const cat = cats.find((c) => c.timeOffCategoryID === catID) ?? null;
  const unpaid = isUnpaid(cat);
  const includeOptional = tenantAllowsOptional && options.data?.allowWeekendHolidayDays === true && !unpaid;
  const earliestRequest = (options.data?.earliestRequestDate ?? todayYmd).slice(0, 10);
  const minFrom = categoryEarliest(cat, earliestRequest);
  const maxDay = addDaysYmd(todayYmd, MAX_DAYS_AHEAD);

  const dayRows = useMemo(
    () => expandRequestDays(fromYmd, toYmd, holidayMap, includeOptional),
    [fromYmd, toYmd, holidayMap, includeOptional],
  );
  const dayHours = dayRows.map((d) => ({
    ymd: d.ymd,
    hours: hoursForDay(d, modes[d.ymd], custom[d.ymd], { unpaid, increment }),
  }));
  const totalHours = dayHours.reduce((s, d) => s + d.hours, 0);
  const sentDays = dayHours.filter((d) => d.hours > 0).length;
  const proj = projectBalance({ available: cat ? Number(cat.currentBalanceHours) || 0 : 0, requestHours: totalHours, unpaid });
  const reason = preflightReason({
    category: cat, fromYmd, toYmd, todayYmd, earliestRequestYmd: earliestRequest, blockedPastDates: blockedPast, days: dayHours,
  });
  const formReady = !!balances.data && !!options.data;
  const canSubmit = formReady && roleAllows && !reason && !proj.short && !submitting;

  const options4 = hourOptions(increment);
  const allowHalf = halfDayAllowed(increment);

  const resetErr = () => setSubmitError(null);

  const pickCategory = (id: number) => {
    resetErr();
    setCatID(id);
    const c = cats.find((x) => x.timeOffCategoryID === id) ?? null;
    const min = categoryEarliest(c, earliestRequest);
    if (fromYmd && fromYmd < min) {
      setFromYmd(min);
      if (toYmd < min) setToYmd(min);
    }
  };

  const onFrom = (v: string) => {
    resetErr();
    setFromYmd(v);
    if (v && (!toYmd || toYmd < v)) setToYmd(v);
  };

  const onTo = (v: string) => {
    resetErr();
    setToYmd(v);
  };

  const setMode = (ymd: string, m: DayMode) => {
    resetErr();
    setModes((x) => ({ ...x, [ymd]: m }));
    if (m === 'custom' && custom[ymd] == null) setCustom((x) => ({ ...x, [ymd]: options4.includes(6) ? 6 : HALF_DAY_HOURS }));
  };

  const submit = async () => {
    if (!cat || !canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await createPortalTimeOffRequest(buildRequestBody(cat.timeOffCategoryID, dayHours, notes));
      showToast(`TO-${created.timeOffRequestID} sent for approval`);
      setModes({});
      setCustom({});
      setNotes('');
      const d = nextWorkday(todayYmd, holidayMap);
      setFromYmd(d);
      setToYmd(d);
      setTab('requests');
      await Promise.all([loadRequests(), loadBalances(), loadOptions()]);
    } catch (err) {
      const info = fail(err);
      const data = (err as { response?: { data?: TimeOffRefusal } })?.response?.data;
      setSubmitError(info.kind === 'refused' ? submitRefusalText(data, info.message) : info.message);
    } finally {
      setSubmitting(false);
    }
  };

  const newRequest = () => {
    setTab('balances');
    window.setTimeout(() => fromRef.current?.focus(), 30);
  };

  /* ------------------------------------------------ cancel */
  const [refused, setRefused] = useState<Record<number, string>>({});
  const [cancelTarget, setCancelTarget] = useState<PortalTimeOffRequest | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const openCancel = (r: PortalTimeOffRequest) => {
    setCancelError(null);
    setCancelTarget(r);
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    const id = cancelTarget.timeOffRequestID;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelPortalTimeOffRequest(id);
      setCancelTarget(null);
      showToast(`TO-${id} cancelled`);
      await Promise.all([loadRequests(), loadBalances()]);
    } catch (err) {
      const info = fail(err);
      setCancelError(info.message);
      if (info.kind === 'refused') setRefused((x) => ({ ...x, [id]: info.message }));
    } finally {
      setCancelling(false);
    }
  };

  /* ------------------------------------------------ requests tab filters */
  const [statusFilter, setStatusFilter] = useState<'all' | RequestStatus>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const typeNames = useMemo(() => Array.from(new Set(requests.map((r) => r.categoryName))).sort(), [requests]);
  const shown = requests.filter((r) =>
    (statusFilter === 'all' || requestStatus(r.status) === statusFilter) && (typeFilter === 'all' || r.categoryName === typeFilter));
  const pending = requests.filter((r) => requestStatus(r.status) === 'pending');

  /* ------------------------------------------------ ledger */
  const [ledCat, setLedCat] = useState<number | null>(null);
  const [ledOffset, setLedOffset] = useState(0);
  const [ledger, setLedger] = useState<Loaded<PortalTimeOffLedgerPage>>({ data: null, error: null });
  const [ledBusy, setLedBusy] = useState(false);

  useEffect(() => {
    if (ledCat == null && paidCats.length) setLedCat(paidCats[0].timeOffCategoryID);
  }, [paidCats, ledCat]);

  useEffect(() => {
    if (tab !== 'ledger' || ledCat == null) return;
    let live = true;
    setLedBusy(true);
    getPortalTimeOffLedger(ledCat, ledOffset, LEDGER_LIMIT)
      .then((p) => { if (live) setLedger({ data: p, error: null }); })
      .catch((err) => { if (live) setLedger((l) => ({ data: l.data, error: fail(err) })); })
      .finally(() => { if (live) setLedBusy(false); });
    return () => { live = false; };
  }, [tab, ledCat, ledOffset, fail]);

  /* ------------------------------------------------ pieces */
  const balanceCards = () => {
    if (!balances.data) {
      return (
        <div className="bal-msg">
          {balances.error ? <><AlertTriangle />{balances.error.message}
            <button type="button" className="btn sm" onClick={() => void loadBalances()}><RefreshCw />Retry</button></>
            : <><Clock />Loading your balances…</>}
        </div>
      );
    }
    if (!cats.length) return <div className="bal-msg"><Info />No time off categories are on your policy.</div>;
    return (
      <div className="bal-grid">
        {cats.map((c) => {
          const Icon = catIcon(c.categoryName, isUnpaid(c));
          const p = Number(c.pendingHours ?? 0);
          if (isUnpaid(c)) {
            const gate = c.isRequestable === false;
            return (
              <div key={c.timeOffCategoryID} className="bal" style={{ ['--bc' as string]: colorOf(c) } as React.CSSProperties}>
                <div className="bl"><Icon />{c.categoryName}</div>
                <div className="bv dimv">—</div>
                <div className="bmeta">No balance · approved hours aren't paid</div>
                <div className="bbar"><i style={{ width: 0 }} /></div>
                <div className="bfoot">
                  {p > 0 ? <span className="bpend"><Clock />{f2(p)} h pending</span>
                    : gate ? <span className="bpend warn" title={notRequestableReason(c) ?? ''}><Lock />After paid time is used up</span>
                      : <span className="bmeta">Full 8-hour days</span>}
                </div>
              </div>
            );
          }
          const avail = Number(c.currentBalanceHours) || 0;
          const used = Number(c.annualUsedHours ?? 0);
          const cap = c.maxAnnualUsageHours;
          const meta = c.isEligible === false && c.eligibleOn
            ? `Usable from ${fmtYmdLong(c.eligibleOn)}`
            : cap != null ? `Available · ${f2(c.annualRemainingHours ?? 0)} of ${f2(cap)} h left this year` : 'Available';
          const bar = cap ? Math.min(100, (used / cap) * 100) : avail + used > 0 ? Math.min(100, (avail / (avail + used)) * 100) : 0;
          return (
            <div key={c.timeOffCategoryID} className="bal" style={{ ['--bc' as string]: colorOf(c) } as React.CSSProperties}>
              <div className="bl"><Icon />{c.categoryName}</div>
              <div className="bv">{f2(avail)}<small>h</small></div>
              <div className="bmeta" title={meta}>{meta}</div>
              <div className="bbar"><i style={{ width: `${bar}%` }} /></div>
              <div className="bfoot">
                {p > 0 ? <span className="bpend"><Clock />{f2(p)} h pending</span>
                  : <span className="bmeta">{f2(used)} h used this year</span>}
              </div>
            </div>
          );
        })}
        {yearHolidays && (
          <div className="bal" style={{ ['--bc' as string]: 'var(--c-holiday)' } as React.CSSProperties}>
            <div className="bl"><Sun />Holiday</div>
            {hv.next && hv.eligible ? (
              <>
                <div className="bv">{f2(hv.next.paidHoursIfNotWorked)}<small>h</small></div>
                <div className="bmeta">Next: {hv.next.name} · {fmtYmdDow(hv.next.holidayDate)}</div>
              </>
            ) : hv.next ? (
              <>
                <div className="bv dimv">—</div>
                <div className="bmeta" title={hv.reason ?? ''}>{hv.eligibleAfter ? 'Holiday pay starts after your waiting period' : `${hv.next.name}: ${hv.reason}`}</div>
              </>
            ) : (
              <>
                <div className="bv dimv">—</div>
                <div className="bmeta">No more company holidays this year</div>
              </>
            )}
            <div className="bbar"><i style={{ width: `${hv.eligibleAfter ? hv.tenurePct : hv.next && hv.eligible ? 100 : 0}%` }} /></div>
            <div className="bfoot">
              {hv.eligibleAfter
                ? <span className="bpend warn"><Clock />Eligible after {fmtYmdLong(hv.eligibleAfter)}</span>
                : <span className="bmeta">Company calendar · paid automatically</span>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const catNote = (): { text: string; warn: boolean } => {
    if (!cat) return { text: ' ', warn: false };
    const gate = notRequestableReason(cat);
    if (gate) return { text: gate, warn: true };
    if (unpaid) return { text: "Full 8-hour days only · approved hours aren't paid", warn: false };
    if (cat.isEligible === false && cat.eligibleOn) return { text: `${cat.categoryName} can be used from ${fmtYmdLong(cat.eligibleOn)}`, warn: true };
    const left = cat.annualRemainingHours != null ? ` · ${f2(cat.annualRemainingHours)} h left to use this year` : '';
    return { text: `${f2(cat.currentBalanceHours)} h available${left}`, warn: false };
  };

  const dayList = () => {
    if (!formReady) return <div className="pk-empty fhint">{options.error || balances.error ? "The request form couldn't load." : 'Loading…'}</div>;
    if (!fromYmd || !toYmd) return <div className="pk-empty fhint">Choose the first and last day</div>;
    if (toYmd < fromYmd) return <div className="pk-empty fhint">The last day is before the first day</div>;
    if (!dayRows.length) {
      return (
        <div className="pk-empty fhint">
          {reason && /longer than/.test(reason) ? reason : 'No scheduled workdays in this range — holidays come from the company calendar'}
        </div>
      );
    }
    return dayRows.map((d) => {
      const m = modes[d.ymd] ?? defaultMode(d);
      const h = hoursForDay(d, m, custom[d.ymd], { unpaid, increment });
      const blocked = blockedPast.has(d.ymd) && h > 0;
      const sub = blocked ? 'Week submitted'
        : d.holidayName ? `${d.holidayName} · optional`
          : d.optional ? 'Weekend · optional' : 'Scheduled 8.00 h';
      return (
        <div key={d.ymd} className={`daypick-row${d.optional ? ' opt' : ''}${blocked ? ' bad' : ''}`}>
          <span className="dpl">
            <span className="dpd">{fmtYmdDow(d.ymd)}</span>
            <span className={`dps${blocked ? ' bad' : d.holidayName ? ' hol' : ''}`}>{sub}</span>
          </span>
          <select
            className="select"
            aria-label={`Hours for ${fmtYmdDow(d.ymd)}`}
            value={unpaid ? 'full' : m}
            disabled={unpaid || submitting}
            onChange={(e) => setMode(d.ymd, e.target.value as DayMode)}
          >
            {d.optional && <option value="off">Not taking</option>}
            <option value="full">Full day · 8.00</option>
            {allowHalf && <option value="half">Half day · 4.00</option>}
            {options4.length > 1 && <option value="custom">Custom hours</option>}
          </select>
          {/* Only Custom has its own hours; otherwise the slot stays (hidden) so the row never reflows. */}
          <select
            className="select mono"
            aria-label={`Custom hours for ${fmtYmdDow(d.ymd)}`}
            value={String(h)}
            style={unpaid || m !== 'custom' ? { visibility: 'hidden' } : undefined}
            aria-hidden={unpaid || m !== 'custom' ? true : undefined}
            tabIndex={unpaid || m !== 'custom' ? -1 : undefined}
            disabled={unpaid || m !== 'custom' || submitting}
            onChange={(e) => { resetErr(); setCustom((x) => ({ ...x, [d.ymd]: Number(e.target.value) })); }}
          >
            {h === 0 && <option value="0">0.00</option>}
            {options4.map((o) => <option key={o} value={String(o)}>{f2(o)}</option>)}
          </select>
        </div>
      );
    });
  };

  const hint = (): { text: string; cls: string } => {
    if (!roleAllows) return { text: "Time off requests aren't turned on for your role. Contact payroll.", cls: 'warn' };
    if (submitError) return { text: submitError, cls: 'bad' };
    if (formReady && proj.short) {
      return {
        text: hasUnpaid ? 'Not enough balance — shorten the request or choose Unpaid Time.' : 'Not enough balance — shorten the request.',
        cls: 'bad',
      };
    }
    if (formReady && reason && cat && fromYmd && toYmd) return { text: reason, cls: 'warn' };
    return { text: "You can cancel until that week's timesheet is submitted.", cls: '' };
  };

  const requestForm = () => {
    const note = catNote();
    const h = hint();
    return (
      <div className="card">
        <div className="card-head">
          <span className="ch-ic"><Plus /></span>
          <div className="ch-tx"><h3>Request time off</h3><div className="ch-sub">Full days, half days or custom hours</div></div>
        </div>
        <div className="reqform">
          <div className="field">
            <label>Type</label>
            {cats.length ? (
              <div className="seg" role="group" aria-label="Type of time off">
                {cats.map((c) => {
                  const Icon = catIcon(c.categoryName, isUnpaid(c));
                  return (
                    <button
                      type="button"
                      key={c.timeOffCategoryID}
                      className={`${c.timeOffCategoryID === catID ? 'on' : ''}${c.isRequestable === false ? ' muted' : ''}`}
                      onClick={() => pickCategory(c.timeOffCategoryID)}
                      disabled={submitting}
                    >
                      <Icon />{c.categoryName}
                    </button>
                  );
                })}
              </div>
            ) : <div className="seg"><button type="button" disabled>{balances.data ? 'No categories' : 'Loading…'}</button></div>}
            <span className={`catnote${note.warn ? ' warn' : ''}`} title={note.text}>{note.text}</span>
            <span className="fhint">Holidays come from the company calendar — no request needed.</span>
          </div>
          <div className="r2">
            <div className="field">
              <label htmlFor="etpReqFrom">From</label>
              <input
                id="etpReqFrom" ref={fromRef} className="input" type="date" value={fromYmd} min={minFrom} max={maxDay}
                disabled={!formReady || submitting} onChange={(e) => onFrom(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="etpReqTo">To</label>
              <input
                id="etpReqTo" className="input" type="date" value={toYmd} min={fromYmd || minFrom} max={maxDay}
                disabled={!formReady || submitting} onChange={(e) => onTo(e.target.value)}
              />
            </div>
          </div>
          <div className="field">
            <label>Hours per day</label>
            <div className="daypick">{dayList()}</div>
          </div>
          <div className={`reqsum${proj.short ? ' bad' : ''}`}>
            <div>
              <div className="rsv">{f2(totalHours)}</div>
              <div className="rsl">hours · {sentDays} day{sentDays === 1 ? '' : 's'}</div>
            </div>
            <span className="grow" />
            <div className="afterbal">
              {unpaid ? <>Unpaid<b>no balance used</b></>
                : <>{cat?.categoryName ?? 'Balance'} after this<b>{proj.after == null ? '—' : `${f2(proj.after)} h`}</b></>}
            </div>
          </div>
          <div className="field">
            <label htmlFor="etpReqNote">Note to {supervisorName ?? 'your approver'}</label>
            <textarea
              id="etpReqNote" className="input" placeholder="Coverage plan or anything helpful" value={notes} maxLength={1000}
              disabled={submitting} onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <span className={`reqhint${h.cls ? ` ${h.cls}` : ''}`} role={h.cls === 'bad' ? 'alert' : undefined} title={h.text}>{h.text}</span>
          <button type="button" className="btn primary reqsubmit" disabled={!canSubmit} onClick={() => void submit()}>
            <Send />{submitting ? 'Sending…' : 'Submit request'}
          </button>
        </div>
      </div>
    );
  };

  const reqRow = (r: PortalTimeOffRequest, full: boolean) => {
    const st = requestStatus(r.status);
    const pill = STATUS_PILL[st];
    const Icon = catIcon(r.categoryName, isUnpaid(r));
    const c = cancelState(r, blockedPast, refused[r.timeOffRequestID]);
    const who = st === 'pending' ? 'waiting for approval' : r.reviewedByName ? `decided by ${r.reviewedByName}` : null;
    const sub = [
      daysLabel(r),
      r.submittedAt ? `submitted ${fmtInstant(r.submittedAt, zone)}` : null,
      who,
      `TO-${r.timeOffRequestID}`,
    ].filter(Boolean).join(' · ');
    const thread = full ? (
      <>
        {r.notes && (
          <div className="reqnote">
            <span className="rn-av">{initials(self ? `${self.name.first} ${self.name.last}` : 'You')}</span>
            <span><b>You</b>{r.submittedAt ? ` · ${fmtInstant(r.submittedAt, zone)}` : ''}<br />{r.notes}</span>
          </div>
        )}
        {r.reviewedByName && r.reviewerNotes && (
          <div className="reqnote sup">
            <span className="rn-av">{initials(r.reviewedByName)}</span>
            <span><b>{r.reviewedByName}</b>{r.reviewedAt ? ` · ${fmtInstant(r.reviewedAt, zone)}` : ''}<br />{r.reviewerNotes}</span>
          </div>
        )}
        {refused[r.timeOffRequestID] && (
          <div className="reqnote warn">
            <span className="rn-av"><Lock /></span>
            <span><b>Can't cancel</b><br />{refused[r.timeOffRequestID]}</span>
          </div>
        )}
      </>
    ) : null;
    const hasThread = full && (r.notes || (r.reviewedByName && r.reviewerNotes) || refused[r.timeOffRequestID]);
    return (
      <div key={r.timeOffRequestID} className={`reqrow${full ? '' : ' compact'}`}>
        <span className="rq-ic"><Icon /></span>
        <span className="rq-m">
          <span className="rq-t" style={{ display: 'block' }}>{r.categoryName} · {rangeLabel(r.startDate, r.endDate)}</span>
          <span className="rq-s" style={{ display: 'block' }}>{sub}</span>
        </span>
        <span className="rq-h">{f2(r.totalHours ?? requestDays(r).reduce((s, d) => s + d.hours, 0))}</span>
        <span className="rq-st"><span className={`pill ${pill.tone}`}>{pill.label}</span></span>
        {full && (
          <span className="rq-act">
            {c.kind === 'ok' && (
              <button type="button" className="btn sm" onClick={() => openCancel(r)}>Cancel request</button>
            )}
            {c.kind === 'locked' && (
              <span className="nocancel" title={refused[r.timeOffRequestID] ?? 'Ask your supervisor or payroll to change it'}><Lock />{c.why}</span>
            )}
          </span>
        )}
        {hasThread && <span className="rq-thread">{thread}</span>}
      </div>
    );
  };

  const sideCards = () => (
    <div className="stack to-side">
      <div className="card">
        <div className="card-head">
          <span className="ch-ic"><Clock /></span>
          <div className="ch-tx"><h3>Awaiting approval</h3></div>
          {pending.length > 0 && <div className="right"><span className="tabn">{pending.length}</span></div>}
        </div>
        <div className="cbody">
          {pending.length ? pending.map((r) => reqRow(r, false))
            : <div className="pk-empty" style={{ padding: 18 }}>{requestsLoaded ? 'Nothing pending' : 'Loading…'}</div>}
        </div>
      </div>
      <div className="card">
        <div className="card-head">
          <span className="ch-ic"><Sun /></span>
          <div className="ch-tx"><h3>Company holidays · {year}</h3><div className="ch-sub">Company calendar · applied automatically</div></div>
        </div>
        <div className="cbody">
          {yearHolidays == null ? <div className="pk-empty" style={{ padding: 18 }}>Loading…</div>
            : yearHolidays.length === 0 ? <div className="pk-empty" style={{ padding: 18 }}>No company holidays on your calendar</div>
              : [...yearHolidays].sort((a, b) => (a.holidayDate < b.holidayDate ? -1 : 1)).map((h) => {
                const d = h.holidayDate.slice(0, 10);
                const why = holidayReason(h);
                return (
                  <div key={`${h.holidayCalendarID}-${d}`} className={`hol-row${d < todayYmd ? ' past' : ''}`}>
                    <span className="hd">{fmtYmdDow(d)}</span>
                    <span className="hn" title={h.name}>{h.name}</span>
                    {why ? <span className="pill warn" title={why}>Not eligible</span>
                      : <span className="pill ok">{f2(h.paidHoursIfNotWorked)} h paid</span>}
                  </div>
                );
              })}
        </div>
        {hv.eligibleAfter && <div className="ot-foot">Holiday pay starts {fmtYmdLong(hv.eligibleAfter)}.</div>}
      </div>
    </div>
  );

  const requestsView = () => (
    <>
      <div className="toolbar">
        <div className="inline-field">
          Status
          <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | RequestStatus)}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="inline-field">
          Type
          <select className="select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All</option>
            {typeNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <span className="grow" />
        <span className="fhint">Cancel any request until that week's timesheet is submitted.</span>
      </div>
      <div className="card">
        {requestsError && !requests.length ? (
          <div className="list-msg"><AlertTriangle />{requestsError.message}
            <button type="button" className="btn sm" onClick={() => void loadRequests()}><RefreshCw />Retry</button></div>
        ) : !requestsLoaded && !requests.length ? (
          <div className="list-msg"><Clock />Loading your requests…</div>
        ) : shown.length ? shown.map((r) => reqRow(r, true)) : (
          <div className="list-msg"><Inbox />{requests.length ? 'No requests match these filters.' : "You haven't requested time off yet."}</div>
        )}
      </div>
    </>
  );

  const ledgerRow = (e: PortalTimeOffLedgerEntry, k: number) => {
    const kind = ledgerKind(e.kind);
    const isPending = e.kind === 'pending';
    // A pending row's date is the request's start date (a calendar day); the rest are instants.
    const when = isPending ? fmtYmdLong(e.date) : fmtInstant(e.date, zone);
    const h = Number(e.hours) || 0;
    return (
      <tr key={`${e.kind}-${e.timeOffRequestID ?? ''}-${e.date}-${k}`} className={isPending ? 'pend' : undefined}>
        <td className="lt mono" style={{ whiteSpace: 'nowrap' }}>{when}</td>
        <td><span className={`pill ${kind.tone}`}>{kind.label}</span></td>
        <td>{e.label}{e.notes ? <span className="ls">{e.notes}</span> : null}</td>
        <td className="mono">{e.timeOffRequestID ? `TO-${e.timeOffRequestID}` : ''}</td>
        <td className={`num ${h >= 0 ? 'pos' : 'neg'}`}>{h >= 0 ? '+' : '−'}{f2(Math.abs(h))}</td>
        <td className="bal">{e.balanceAfter == null ? '—' : f2(e.balanceAfter)}</td>
      </tr>
    );
  };

  const ledgerView = () => {
    const page = ledger.data;
    const rows = page ? [...(ledOffset === 0 ? page.pending : []), ...page.items] : [];
    return (
      <>
        <div className="toolbar">
          {paidCats.length ? (
            <div className="seg" role="group" aria-label="Category">
              {paidCats.map((c) => (
                <button
                  type="button" key={c.timeOffCategoryID} className={c.timeOffCategoryID === ledCat ? 'on' : ''}
                  onClick={() => { setLedCat(c.timeOffCategoryID); setLedOffset(0); }}
                >
                  {c.categoryName}
                </button>
              ))}
            </div>
          ) : null}
          <span className="grow" />
          <span className="fhint">
            {page ? <>Balance now <b className="mono" style={{ color: 'var(--text)' }}>{f2(page.currentBalanceHours)} h</b> · pending requests are pinned on top</> : ' '}
          </span>
        </div>
        <div className="card">
          <div className={`led-body${ledBusy ? ' busy' : ''}`}>
            {!paidCats.length ? (
              <div className="list-msg"><Info />{balances.data ? 'No categories with a balance on your policy.' : 'Loading…'}</div>
            ) : ledger.error && !page ? (
              <div className="list-msg"><AlertTriangle />{ledger.error.message}</div>
            ) : !page ? (
              <div className="list-msg"><Clock />Loading history…</div>
            ) : rows.length === 0 ? (
              <div className="list-msg"><FileSpreadsheet />No history for this category yet.</div>
            ) : (
              <div className="led-wrap">
                <table className="led">
                  <thead>
                    <tr>
                      <th>Posted</th><th>Type</th><th>Details</th><th>Request</th>
                      <th style={{ textAlign: 'right' }}>Hours</th><th style={{ textAlign: 'right' }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>{rows.map(ledgerRow)}</tbody>
                </table>
              </div>
            )}
          </div>
          <div className="pager">
            <span className="grow">
              {ledger.error && page ? <span style={{ color: '#a8331f' }}>{ledger.error.message}</span>
                : page ? pageLabel(ledOffset, page.items.length, page.total) : ' '}
            </span>
            <button type="button" className="btn sm" disabled={ledBusy || ledOffset === 0} onClick={() => setLedOffset((o) => Math.max(0, o - LEDGER_LIMIT))}>
              <ChevronLeft />Newer
            </button>
            <button
              type="button" className="btn sm"
              disabled={ledBusy || !page || ledOffset + page.items.length >= page.total}
              onClick={() => setLedOffset((o) => o + LEDGER_LIMIT)}
            >
              Older<ChevronRight />
            </button>
          </div>
        </div>
      </>
    );
  };

  /* ------------------------------------------------ render */
  const tabs: { key: Tab; label: React.ReactNode; Icon: LucideIcon }[] = [
    { key: 'balances', label: 'Balances & request', Icon: Wallet },
    { key: 'requests', label: <>My requests <span className="tabn">{requests.length}</span></>, Icon: Inbox },
    { key: 'ledger', label: 'Accrual history', Icon: FileSpreadsheet },
  ];

  return (
    <div className="etp">
      <div className="page-head">
        <div className="ph-ic"><CalendarCheck /></div>
        <div className="ph-tx">
          <h1>Time Off</h1>
          <div className="ph-sub">
            Balances as of {fmtYmdLong(todayYmd)} · requests go to {supervisorName ?? 'your approver'}
          </div>
        </div>
        <div className="ph-actions">
          <button type="button" className="btn primary" onClick={newRequest} disabled={!roleAllows}><Plus />New request</button>
        </div>
      </div>

      <div className="tabs" role="tablist">
        {tabs.map(({ key, label, Icon }) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={`tab${tab === key ? ' on' : ''}`} onClick={() => setTab(key)}>
            <Icon />{label}
          </button>
        ))}
      </div>

      {tab === 'balances' && (
        <div className="stack">
          {balanceCards()}
          <div className="grid2">
            {requestForm()}
            {sideCards()}
          </div>
        </div>
      )}
      {tab === 'requests' && requestsView()}
      {tab === 'ledger' && ledgerView()}

      {cancelTarget && (
        <div className="etp-modal-bg" role="presentation">
          <div className="etp-modal" role="dialog" aria-modal="true" aria-labelledby="etp-cancel-title">
            <div className="modal-head">
              <div className="mi warn"><CalendarX /></div>
              <div>
                <h3 id="etp-cancel-title">Cancel TO-{cancelTarget.timeOffRequestID}?</h3>
                <div className="ms">{cancelTarget.categoryName} · {rangeLabel(cancelTarget.startDate, cancelTarget.endDate)} · {f2(cancelTarget.totalHours)} h</div>
              </div>
              <button type="button" className="x" aria-label="Close" onClick={() => setCancelTarget(null)} disabled={cancelling}><X /></button>
            </div>
            <div className="modal-body fixed150">
              <p className="mtext">
                {isUnpaid(cancelTarget)
                  ? 'The request is withdrawn. Unpaid Time uses no balance.'
                  : `The request is withdrawn and its ${f2(cancelTarget.totalHours)} h go back to your ${cancelTarget.categoryName} balance.`}
              </p>
              <p className="mtext err" role="alert" style={{ visibility: cancelError ? 'visible' : 'hidden' }}>{cancelError ?? ' '}</p>
            </div>
            <div className="modal-foot">
              {cancelError && refused[cancelTarget.timeOffRequestID] ? (
                <button type="button" className="btn wbtn" onClick={() => setCancelTarget(null)}>Close</button>
              ) : (
                <>
                  <button type="button" className="btn" onClick={() => setCancelTarget(null)} disabled={cancelling}>Keep request</button>
                  <button type="button" className="btn danger wbtn" onClick={() => void confirmCancel()} disabled={cancelling}>
                    {cancelling ? 'Cancelling…' : 'Cancel request'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {toast.node}
    </div>
  );
};

export default MyTimeOffPage;
